import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { useSeo } from "@/lib/useSeo";
import { ArrowLeft, ArrowRight } from "lucide-react";

export default function Manifesto() {
  useSeo({
    title: "Manifesto — SuiteCrowd",
    description:
      "Il manifesto di SuiteCrowd: perché abbiamo fondato il primo club italiano di co-finanziamento turistico. Le 4 regole del pre-acquisto di lusso.",
    canonical: "https://suitecrowd.com/manifesto",
  });

  return (
    <div className="min-h-screen bg-white text-[#121212]">
      <nav className="border-b border-[#E5E5E5] bg-white/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex justify-between items-center">
          <Logo variant="dark" size="md" />
          <Link to="/" className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 hover:text-[#121212] inline-flex items-center gap-2 transition-colors">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="sc-overline mb-6">Manifesto</div>
        <h1 className="font-display font-light text-5xl md:text-6xl mb-8" style={{ letterSpacing: "-0.03em" }}>
          Un club per un turismo che sceglie da che parte stare.
        </h1>

        <div className="space-y-14 text-[16px] leading-[1.9] text-neutral-700 mt-16">
          <section>
            <div className="sc-overline mb-4 text-[#0B192C]">Il problema</div>
            <p>
              Ogni anno, centinaia di hotel storici italiani chiudono. Non per mancanza di
              ospiti — ma per mancanza di capitale con cui rimettere in ordine facciate liberty,
              impianti degli anni '70, camere che hanno visto passare tre generazioni. Il credito
              bancario non li vede più. I fondi immobiliari cercano ticket da 10 milioni, non da
              300 mila. E così, uno dopo l'altro, spariscono.
            </p>
            <p className="mt-5">
              Nel frattempo, migliaia di persone in Italia sognano quel tipo di soggiorno.
              Una facciata liberty affacciata sull'Adriatico. Un giardino mediterraneo di 20.000
              metri quadri. Un centro benessere sotterraneo scavato nella roccia. Cose che, se
              costruite oggi da zero, costerebbero troppo per essere accessibili.
            </p>
          </section>

          <section>
            <div className="sc-overline mb-4 text-[#0B192C]">La soluzione</div>
            <p>
              SuiteCrowd collega i due lati. Da una parte gli hotel che hanno bisogno di
              capitale. Dall'altra le persone che sarebbero felici di anticipare una quota
              simbolica per garantirsi un soggiorno futuro. Nel mezzo, un conto escrow
              indipendente che protegge entrambi.
            </p>
            <p className="mt-5">
              Non è filantropia. Non è investimento speculativo. È un pre-acquisto collettivo
              di ospitalità: paghi oggi qualcosa che riceverai tra sei o dodici mesi, quando i
              lavori saranno completati. Nel frattempo, i tuoi soldi non sono nelle mani
              dell'hotel — sono congelati in un conto di garanzia gestito da Stripe.
            </p>
          </section>

          <section className="border-t border-[#E5E5E5] pt-14">
            <div className="sc-overline mb-6 text-[#0B192C]">Le nostre 4 regole</div>

            <div className="space-y-10">
              {[
                { n: "01", t: "Non è un investimento speculativo", d: "Non promettiamo rendimenti finanziari, non emettiamo token, non siamo un prodotto finanziario. Quello che ricevi è un soggiorno — non un ritorno sul capitale." },
                { n: "02", t: "Non è un dono a fondo perduto", d: "Se l'hotel non riapre nei tempi previsti, ricevi indietro il 100% della quota versata. Il rischio esecutivo è dell'hotel, non tuo." },
                { n: "03", t: "I soldi sono tuoi finché non partono i lavori", d: "I fondi restano congelati in conto escrow indipendente. Vengono svincolati all'hotel solo al raggiungimento del target o all'avanzamento certificato dei lavori. Zero anticipi ciechi." },
                { n: "04", t: "Il lusso non deve essere per pochi, ma per pochi alla volta", d: "Non vogliamo democratizzare tutto. Il club resta piccolo, curato, esclusivo. Ma vogliamo che chi ci crede possa entrare con una quota accessibile — non riservata a chi ha 50.000€ da investire." },
              ].map((r) => (
                <div key={r.n} className="grid grid-cols-12 gap-6">
                  <div className="col-span-2 md:col-span-1">
                    <div className="font-display text-2xl font-extralight text-[#0B192C]">{r.n}</div>
                  </div>
                  <div className="col-span-10 md:col-span-11">
                    <h3 className="font-display font-normal text-xl mb-2" style={{ letterSpacing: "-0.01em" }}>
                      {r.t}
                    </h3>
                    <p className="text-neutral-600 text-[15px] leading-relaxed">{r.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="border-t border-[#E5E5E5] pt-14">
            <div className="sc-overline mb-4 text-[#0B192C]">Il nostro impegno</div>
            <p>
              Ogni euro raccolto è visibile in tempo reale. Ogni cantiere è documentato con
              foto datate pubblicate dal titolare della struttura. Ogni voucher è nominativo
              e legato a un contratto chiaro. Non nascondiamo condizioni in note a piè di
              pagina. Non facciamo marketing dei rendimenti perché non ce ne sono.
            </p>
            <p className="mt-5">
              Facciamo una cosa sola: trasformiamo il capitale disperso di migliaia di
              persone che amano il lusso italiano in una forza collettiva capace di
              rimettere in moto strutture che meritano di rinascere. Non è tanto. Ma è
              qualcosa che oggi non esiste.
            </p>
          </section>

          <section className="border-t border-[#E5E5E5] pt-14">
            <p className="font-display font-extralight text-3xl italic text-[#0B192C]" style={{ letterSpacing: "-0.02em" }}>
              Se ti riconosci in queste parole, entra nel club.
            </p>
            <div className="mt-8 flex flex-col md:flex-row gap-4">
              <Link to="/" className="sc-btn-primary inline-flex items-center gap-2" data-testid="manifesto-cta-waitlist">
                Entra nella waitlist <ArrowRight size={14} />
              </Link>
              <Link to="/hotel-pilota" className="sc-btn-ghost inline-flex items-center gap-2">
                Vedi il progetto pilota
              </Link>
            </div>
          </section>
        </div>
      </article>

      <Footer />
    </div>
  );
}
